import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { formatCurrency } from '../../lib/formatters.js';

interface ExternalTask {
  id: string;
  constraintName: string;
  description: string;
  routingRole: string;
  status: 'PENDING' | 'CLEARED' | 'BLOCKED';
  claimSubmission: {
    id: string;
    claimNumber: string;
    claimType: string;
    approvedAmount: string | null;
    isoCurrencyCode: string;
    org: { name: string; legalName: string | null };
    scheme: { name: string } | null;
  };
}

function StatusPill({ status }: { status: string }) {
  const { t } = useTranslation();
  const colours: Record<string, string> = {
    PENDING: 'bg-amber-100 text-amber-700',
    CLEARED: 'bg-success/10 text-success',
    BLOCKED: 'bg-danger/10 text-danger',
  };
  return (
    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${colours[status] ?? 'bg-slate-100 text-slate-500'}`}>
      {t(`approver.externalVerification.statuses.${status}`, { defaultValue: status })}
    </span>
  );
}

function ResolvePanel({ task, onResolved }: { task: ExternalTask; onResolved: () => void }) {
  const { t } = useTranslation();
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState<'CLEARED' | 'BLOCKED' | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  async function resolve(resolution: 'CLEARED' | 'BLOCKED') {
    setSubmitting(resolution);
    setMessage(null);
    try {
      await api.post(`/approver/external-verification-tasks/${task.id}/resolve`, { resolution, notes: notes || undefined });
      setMessage(resolution === 'CLEARED'
        ? t('approver.externalVerification.resolvedCleared')
        : t('approver.externalVerification.resolvedBlocked'));
      setTimeout(() => onResolved(), 1500);
    } catch (e: unknown) {
      setMessage((e as Error).message ?? 'Error');
      setSubmitting(null);
    }
  }

  return (
    <div className="mt-3 p-3 bg-white border border-hairline rounded space-y-2">
      <div>
        <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">
          {t('approver.externalVerification.notesLabel')}
        </label>
        <textarea
          rows={2}
          className="w-full border border-hairline rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-copper resize-none"
          placeholder={t('approver.externalVerification.notesPlaceholder')}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>
      {message && (
        <p className={`text-xs font-medium ${message.includes('rejected') || message.includes('ditolak') ? 'text-danger' : 'text-success'}`}>
          {message}
        </p>
      )}
      <div className="flex gap-2">
        <button
          onClick={() => resolve('CLEARED')}
          disabled={!!submitting}
          className="px-3 py-1.5 text-xs font-semibold bg-success text-white rounded hover:bg-green-700 transition-colors disabled:opacity-50"
        >
          {submitting === 'CLEARED' ? '...' : t('approver.externalVerification.clearButton')}
        </button>
        <button
          onClick={() => resolve('BLOCKED')}
          disabled={!!submitting}
          className="px-3 py-1.5 text-xs font-semibold bg-danger text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50"
        >
          {submitting === 'BLOCKED' ? '...' : t('approver.externalVerification.blockButton')}
        </button>
      </div>
    </div>
  );
}

export function ExternalVerificationQueuePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<ExternalTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get<{ items: ExternalTask[] }>('/approver/external-verification-tasks')
      .then((data) => setTasks(data.items ?? []))
      .catch(() => setTasks([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const pending = tasks.filter((t) => t.status === 'PENDING');
  const resolved = tasks.filter((t) => t.status !== 'PENDING');

  function renderTable(rows: ExternalTask[], showResolve: boolean) {
    return (
      <div className="overflow-x-auto border border-hairline rounded">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-hairline text-[10px] uppercase tracking-wider text-slate-500 bg-bone">
              <th className="text-left px-4 py-3">{t('approver.externalVerification.claimNumber')}</th>
              <th className="text-left px-4 py-3">{t('approver.queue.distributor')}</th>
              <th className="text-left px-4 py-3">{t('approver.externalVerification.constraint')}</th>
              <th className="text-left px-4 py-3">{t('approver.externalVerification.routingRole')}</th>
              <th className="text-left px-4 py-3">{t('approver.externalVerification.status')}</th>
              <th className="text-right px-4 py-3">{t('settlement.table.approvedAmount')}</th>
              {showResolve && <th className="px-4 py-3" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-hairline">
            {rows.map((task) => (
              <>
                <tr
                  key={task.id}
                  className="hover:bg-bone transition-colors"
                >
                  <td className="px-4 py-3">
                    <button
                      onClick={() => navigate(`/approver/claims/${task.claimSubmission.id}`)}
                      className="font-mono text-xs text-copper hover:underline"
                    >
                      {task.claimSubmission.claimNumber}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600 max-w-[160px] truncate">
                    {task.claimSubmission.org.legalName ?? task.claimSubmission.org.name}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs font-semibold text-ink">{task.constraintName}</div>
                    <div className="text-[10px] text-slate-500 truncate max-w-[220px]">{task.description}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {t(`approver.externalVerification.roles.${task.routingRole}`, { defaultValue: task.routingRole })}
                  </td>
                  <td className="px-4 py-3"><StatusPill status={task.status} /></td>
                  <td className="px-4 py-3 text-right font-mono text-xs">
                    {task.claimSubmission.approvedAmount
                      ? formatCurrency(Number(task.claimSubmission.approvedAmount), task.claimSubmission.isoCurrencyCode)
                      : '—'}
                  </td>
                  {showResolve && (
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setExpanded((prev) => prev === task.id ? null : task.id)}
                        className="text-xs text-copper hover:underline font-medium"
                      >
                        {expanded === task.id ? '▲' : t('approver.externalVerification.resolve')}
                      </button>
                    </td>
                  )}
                </tr>
                {showResolve && expanded === task.id && (
                  <tr key={`${task.id}-panel`}>
                    <td colSpan={7} className="px-4 pb-3 bg-bone/50">
                      <ResolvePanel
                        task={task}
                        onResolved={() => { setExpanded(null); load(); }}
                      />
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <button
          type="button"
          onClick={() => navigate('/approver')}
          className="text-xs text-slate-500 hover:text-copper mb-3 transition-colors block"
        >
          ← {t('approver.dashboard.title')}
        </button>
        <h1 className="font-serif italic text-3xl text-ink">
          {t('approver.externalVerification.title')}
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          {t('approver.externalVerification.subtitle')}
        </p>
      </div>

      {loading ? (
        <div className="space-y-2 animate-pulse">
          {[0, 1, 2].map((i) => <div key={i} className="h-12 bg-slate-100 rounded" />)}
        </div>
      ) : (
        <>
          {pending.length === 0 ? (
            <div className="py-16 text-center border border-hairline rounded bg-bone">
              <p className="text-sm text-slate-400">{t('approver.externalVerification.empty')}</p>
            </div>
          ) : (
            renderTable(pending, true)
          )}

          {resolved.length > 0 && (
            <div>
              <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">
                Resolved
              </h2>
              {renderTable(resolved, false)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
